import clsx from 'clsx';
import type { EpfPayment } from '../../../../../../store/dashboard/compliancesBefe/types';
import { monthKeyToDisplay, periodHeaderToMonthKey } from '../../../../../../utils/dateUtils';
import styles from '../scss/MonthlyView.module.scss';
import { EPF_DEFAULT_WITHOUT_ESTABLISHMENT, EPF_FILING_STATUS_TITLE, ESTABLISHMENT_ID_PREFIX } from './constants';
import { formatEmployees, formatLacCr, formatPaymentStatus, getPaymentTone } from './epfFormatters';

const TONE_CLASS = {
    onTime: styles.OnTime,
    delayed: styles.Delayed,
    default: styles.Defaulted,
};

/** Payments grouped by establishment, keeping UW order; Karza defaults have no establishment. */
const groupByEstablishment = (payments: EpfPayment[]): [string | null, EpfPayment[]][] => {
    const groups = new Map<string | null, EpfPayment[]>();
    payments.forEach((payment) => {
        const id = payment.establishmentId ?? null;
        if (!groups.has(id)) groups.set(id, []);
        groups.get(id)!.push(payment);
    });
    return Array.from(groups.entries());
};

const PaymentRow = ({ payment }: { payment: EpfPayment }) => (
    <div className={styles.EpfPaymentRow}>
        <span>{payment.numberOfEmployee !== null && `${formatEmployees(payment.numberOfEmployee)} Employees`}</span>
        <span className={styles.EpfAmount}>{payment.totalAmount !== null && formatLacCr(payment.totalAmount)}</span>
        <span className={clsx(styles.Badge, TONE_CLASS[getPaymentTone(payment)])}>{formatPaymentStatus(payment)}</span>
    </div>
);

const EpfHoverContent = ({ xValue, payments }: { xValue: string; payments: EpfPayment[] }) => (
    <div className={clsx(styles.Tooltip, styles.EpfTooltip)}>
        <div className={styles.TopText}>
            {EPF_FILING_STATUS_TITLE} <span className={styles.Divider}>|</span>{' '}
            <span className={styles.Month}>{monthKeyToDisplay(periodHeaderToMonthKey(xValue))}</span>
        </div>
        {groupByEstablishment(payments).map(([establishmentId, group]) =>
            establishmentId === null ? (
                <div className={styles.EpfGroup} key="no-establishment">
                    <div className={styles.EpfPaymentRow}>
                        <span>{EPF_DEFAULT_WITHOUT_ESTABLISHMENT}</span>
                    </div>
                </div>
            ) : (
                <div className={styles.EpfGroup} key={establishmentId}>
                    <div className={styles.EpfGroupTitle}>{`${ESTABLISHMENT_ID_PREFIX} ${establishmentId}`}</div>
                    {group.map((payment, i) => (
                        <PaymentRow payment={payment} key={`${establishmentId}-${i}`} />
                    ))}
                </div>
            ),
        )}
    </div>
);

export default EpfHoverContent;
